// Saved Jobs React Query Hooks

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { unifiedApiService } from '../../services/unified/unifiedApiService';
import type { SavedJob, SaveJobResponse } from '../../services/unified/savedJobsModule';

// Query keys
export const savedJobsKeys = {
  all: ['savedJobs'] as const,
  list: () => [...savedJobsKeys.all, 'list'] as const,
  check: (jobId: string) => [...savedJobsKeys.all, 'check', jobId] as const,
};

/**
 * Hook to get all saved jobs for the current user
 */
export function useSavedJobs() {
  return useQuery<SavedJob[], Error>({
    queryKey: savedJobsKeys.list(),
    queryFn: () => unifiedApiService.savedJobs.getSavedJobs(),
  });
}

/**
 * Hook to check if a specific job is saved
 */
export function useIsJobSaved(jobId: string, enabled = true) {
  return useQuery<boolean, Error>({
    queryKey: savedJobsKeys.check(jobId),
    queryFn: () => unifiedApiService.savedJobs.isJobSaved(jobId),
    enabled: enabled && !!jobId,
  });
}

/**
 * Hook to save a job
 */
export function useSaveJob() {
  const queryClient = useQueryClient();

  return useMutation<SaveJobResponse, Error, string>({
    mutationFn: (jobId: string) => unifiedApiService.savedJobs.saveJob(jobId),
    onSuccess: (_, jobId) => {
      // Invalidate saved jobs list
      queryClient.invalidateQueries({ queryKey: savedJobsKeys.list() });
      // Update the check query for this specific job
      queryClient.setQueryData(savedJobsKeys.check(jobId), true);
    },
  });
}

/**
 * Hook to unsave a job
 */
export function useUnsaveJob() {
  const queryClient = useQueryClient();

  return useMutation<SaveJobResponse, Error, string>({
    mutationFn: (jobId: string) => unifiedApiService.savedJobs.unsaveJob(jobId),
    onSuccess: (_, jobId) => {
      // Invalidate saved jobs list
      queryClient.invalidateQueries({ queryKey: savedJobsKeys.list() });
      // Update the check query for this specific job
      queryClient.setQueryData(savedJobsKeys.check(jobId), false);
    },
  });
}

/**
 * Hook to toggle save status
 */
export function useToggleSaveJob() {
  const queryClient = useQueryClient();

  return useMutation<SaveJobResponse, Error, { jobId: string; currentlySaved: boolean }>({
    mutationFn: ({ jobId, currentlySaved }) => 
      unifiedApiService.savedJobs.toggleSaveJob(jobId, currentlySaved),
    onSuccess: (response, { jobId }) => {
      // Invalidate saved jobs list
      queryClient.invalidateQueries({ queryKey: savedJobsKeys.list() });
      // Update the check query for this specific job
      queryClient.setQueryData(savedJobsKeys.check(jobId), response.saved);
    },
  });
}
